import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle, Zap, FileText, Upload } from 'lucide-react';
import Header from '../components/Header';

export default function HowToUse() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card border border-border rounded-lg p-8"
          >
            <h1 className="text-4xl font-heading font-bold mb-6">
              How To Use Test Case Generator
            </h1>

            <div className="prose prose-invert max-w-none space-y-8 text-foreground">
              {/* Main Intro */}
              <div>
                <h2 className="text-2xl font-heading font-semibold mb-4 text-blue-400">
                  How to Create Import-Ready Test Cases for JIRA and Azure DevOps (The 15-Second Workflow)
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  The largest bottleneck in a software development cycle is frequently the creation of manual test cases. By converting your unstructured requirements into formatted, structured CSV files that you can upload straight to your project management software,{' '}
                  <a href="https://mytestcase.com" className="text-blue-500 hover:underline font-medium">
                    MyTestCase.com
                  </a>{' '}
                  automates this process.
                </p>
                <p className="text-lg font-medium mt-4 text-green-400">
                  Reduce the time you spend on QA documentation by 90% by following these four steps.
                </p>
              </div>

              {/* Step 1 */}
              <div className="bg-secondary/30 rounded-lg p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <h3 className="text-xl font-heading font-semibold">Step 1: Input Your Requirements</h3>
                </div>
                <ul className="space-y-3 ml-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                    <span>
                      <strong>Enter Your Needs:</strong> To begin, copy and paste your feature description or user story into the "Description" field.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Zap className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
                    <span>
                      <strong>Pro Tip:</strong> Include your Acceptance Criteria to increase accuracy. These are used by the AI to guarantee that each "Definition of Done" has an associated test step.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileText className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                    <span>
                      <strong>Optional:</strong> To direct the generator, enter specific test scenarios in the "High-level Test Scenarios" box to align the output.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Step 2 */}
              <div className="bg-secondary/30 rounded-lg p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <h3 className="text-xl font-heading font-semibold">Step 2: Choose Your Platform</h3>
                </div>
                <p className="mb-4 text-muted-foreground">Choose your target platform before clicking "generate":</p>
                <ul className="space-y-3 ml-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                    <span>
                      <strong>Azure DevOps:</strong> The ADO CSV schema (Title, Steps, Expected Result, Priority) is optimized for ADO.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-purple-500 shrink-0 mt-0.5" />
                    <span>
                      <strong>JIRA:</strong> Designed for imports into Zephyr, Xray, and JIRA.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Step 3 */}
              <div className="bg-secondary/30 rounded-lg p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <h3 className="text-xl font-heading font-semibold">Step 3: Generate, Review, and Download</h3>
                </div>
                <p className="mb-4 text-muted-foreground">
                  Click "Generate and Download." The AI will create a thorough list of test cases in less than 15 seconds.
                </p>
                <ul className="space-y-3 ml-4">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 shrink-0 mt-0.5" />
                    <span>
                      <strong>Review:</strong> Verify the output of test cases and make required changes if any.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Upload className="h-5 w-5 text-blue-500 shrink-0 mt-0.5" />
                    <span>
                      <strong>Download:</strong> Save the file that is created.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileText className="h-5 w-5 text-purple-500 shrink-0 mt-0.5" />
                    <span>
                      <strong>Import:</strong> Go to your Azure DevOps or JIRA board, select "Import Work Items," and upload your file.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Why Use Section */}
              <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-lg p-6 border border-blue-500/30">
                <h3 className="text-xl font-heading font-semibold mb-4">
                  Why Use{' '}
                  <a href="https://mytestcase.com" className="text-blue-400 hover:underline">
                    MyTestCase.com
                  </a>?
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-600/20 flex items-center justify-center shrink-0">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    </div>
                    <div>
                      <strong className="text-green-400">Simple to Use:</strong>
                      <span className="text-muted-foreground ml-1">
                        Whether you are an ADO user or JIRA user, all you need is to copy and paste your raw requirements here to generate test cases.
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-600/20 flex items-center justify-center shrink-0">
                      <FileText className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <strong className="text-blue-400">Platform Consistency:</strong>
                      <span className="text-muted-foreground ml-1">
                        Get your CSV files to upload to ADO or JIRA without having to struggle with Excel formatting.
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-yellow-600/20 flex items-center justify-center shrink-0">
                      <Zap className="h-5 w-5 text-yellow-500" />
                    </div>
                    <div>
                      <strong className="text-yellow-400">Speed:</strong>
                      <span className="text-muted-foreground ml-1">
                        In less time than it takes to make a cup of coffee, go from a raw requirement to a complete test suite.
                      </span>
                    </div>
                  </li>
                </ul>
              </div>

              {/* CTA Button */}
              <div className="flex justify-center pt-4">
                <Button
                  size="lg"
                  onClick={() => navigate('/')}
                  className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 font-semibold px-8 py-6 text-lg"
                  data-testid="start-generating-button"
                >
                  Start Generating Test Cases
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
